import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { CheckCircle, ArrowRight } from 'lucide-react';

interface ProductsPageProps {
  onPageChange: (page: string) => void;
}

export function ProductsPage({ onPageChange }: ProductsPageProps) {
  const ferrousProducts = [
    {
      name: "Steel Scrap",
      image: "/assets/images/ferrous/Steel Scrap-min.jpeg",
      grades: ["HMS 1&2", "Shredded Steel", "Plate & Structural"],
      description: "High-quality steel scrap materials including heavy melting scrap, shredded steel, and structural steel.",
      specifications: [
        "Carbon content: 0.15% - 0.25%",
        "Density: 7.8 g/cm³",
        "Thickness: Various grades available",
        "Packaging: Baled or loose"
      ]
    },
    {
      name: "Iron Scrap",
      image: "/assets/images/ferrous/Iron Scrap-min.jpeg",
      grades: ["Cast Iron", "Wrought Iron", "Pig Iron"],
      description: "Premium iron scrap materials sourced from reliable suppliers with consistent quality.",
      specifications: [
        "Iron content: 92% - 95%",
        "Low phosphorus content",
        "Various size specifications",
        "Clean and sorted materials"
      ]
    },
    {
      name: "Stainless Steel",
      image: "/assets/images/ferrous/Stainless Steel Scrap-min.jpeg",
      grades: ["304", "316", "430", "201"],
      description: "Various grades of stainless steel scrap for specialized industrial applications.",
      specifications: [
        "Chromium content: 16% - 26%",
        "Nickel content: 6% - 22%",
        "Corrosion resistant",
        "Food grade options available"
      ]
    }
  ];

  const nonFerrousProducts = [
    {
      name: "Copper Scrap",
      image: "/assets/images/non-ferrous/Copper Scrap-min.jpeg",
      grades: ["Millberry", "Birch/Cliff", "Berry", "#1 Copper"],
      description: "Premium copper scrap materials with high purity levels for various industrial uses.",
      specifications: [
        "Purity: 99.9%+",
        "Low oxide content",
        "Bright and clean",
        "Various wire gauges available"
      ]
    },
    {
      name: "Aluminum Scrap",
      image: "/assets/images/non-ferrous/Aluminum Scrap-min.jpeg",
      grades: ["6063", "Tense", "Taint/Tabor", "UBC"],
      description: "High-grade aluminum scrap including extrusions, sheets, and used beverage cans.",
      specifications: [
        "Purity: 95% - 99%",
        "Low iron content",
        "Clean and sorted",
        "Various alloy compositions"
      ]
    },
    {
      name: "Brass Scrap",
      image: "/assets/images/non-ferrous/Brass Scrap-min.jpeg",
      grades: ["Yellow Brass", "Red Brass", "Naval Brass"],
      description: "Quality brass scrap materials for manufacturing and industrial applications.",
      specifications: [
        "Copper content: 60% - 85%",
        "Zinc content: 15% - 40%",
        "Clean and sorted",
        "Various compositions available"
      ]
    },
    {
      name: "Lead Scrap",
      image: "/assets/images/non-ferrous/Lead Scrap-min.jpeg",
      grades: ["Soft Lead", "Hard Lead", "Battery Lead"],
      description: "Lead scrap materials including battery scrap and various lead alloys.",
      specifications: [
        "Purity: 95% - 99%",
        "Low antimony content",
        "Clean materials",
        "Proper safety handling"
      ]
    },
    {
      name: "Zinc Scrap",
      image: "/assets/images/non-ferrous/Zinc Scrap-min.jpeg",
      grades: ["Zinc Die Cast", "New Zinc", "Old Zinc"],
      description: "Various grades of zinc scrap for galvanizing and alloy production.",
      specifications: [
        "Purity: 95% - 99%",
        "Low iron content",
        "Die cast materials",
        "Clean and sorted"
      ]
    },
    {
      name: "Nickel Scrap",
      image: "/assets/images/non-ferrous/Nickel Scrap-min.jpeg",
      grades: ["Nickel Solids", "Nickel Turnings", "Inconel"],
      description: "High-value nickel scrap materials including super alloys and pure nickel.",
      specifications: [
        "Purity: 95% - 99%",
        "Various alloy compositions",
        "Super alloy grades available",
        "Clean materials"
      ]
    }
  ];

  const rubberProducts = [
    {
      name: "3 Cut Tyres",
      image: "/assets/images/rubber/3 cut Tyres-min.jpeg",
      grades: ["Sidewall ×2", "Tread ring", "PCR/TBR"],
      description: "Tyres cut into three pieces (two sidewalls and one tread) for easy loading and pyrolysis feed.",
      specifications: [
        "De-beaded option",
        "Higher loading efficiency",
        "Clean, uniform cuts",
        "Suitable for small reactors"
      ]
    },
    {
      name: "Airplane Tyres",
      image: "/assets/images/rubber/Airplane Tyres-min.jpeg",
      grades: ["Bias", "Radial", "Carcass", "Cut"],
      description: "Decommissioned aircraft tyres supplied as carcasses or pre-cut pieces for high-grade rubber recovery.",
      specifications: [
        "High natural rubber content",
        "Strict de-beading option",
        "Clean, traceable lots",
        "Export-ready packing"
      ]
    },
    {
      name: "Baled Tyres",
      image: "/assets/images/rubber/Baled tyres-min.jpeg",
      grades: ["PCR", "TBR", "Mixed", "Densified"],
      description: "Densified whole tyres compressed for efficient transport and landfill/civil applications.",
      specifications: [
        "Bale weight: 800–1,200 kg",
        "Secure galvanized strapping",
        "PCR/TBR sorted options",
        "Maximize container yield"
      ]
    },
    {
      name: "Conveyor Belts",
      image: "/assets/images/rubber/conveyor belt-min.jpeg",
      grades: ["NN", "EP", "2–4 ply", "800–1600 mm"],
      description: "Used rubber conveyor belts with fabric or steel cord for reuse or size-reduced recycling.",
      specifications: [
        "Rolls or cut sheets",
        "Thickness 8–20 mm typical",
        "Minimal oil/metal contamination",
        "Loadable by container/flatbed"
      ]
    },
    {
      name: "Crumb Rubber",
      image: "/assets/images/rubber/Crumb rubber-min.jpg",
      grades: ["0–1 mm", "1–3 mm", "3–5 mm", "20 mm"],
      description: "Clean rubber crumb processed from end-of-life tyres for compounds, flooring and sports infill.",
      specifications: [
        "Wire-free options available",
        "Low moisture: ≤1%",
        "Consistent particle size grading",
        "Packed in 25 kg bags / jumbo bags"
      ]
    },
    {
      name: "OTR Tyres",
      image: "/assets/images/rubber/Otr tyres-min.jpeg",
      grades: ["Loader", "Dumper", "Dozer", "Mixed sizes"],
      description: "Off-the-road tyres from mining and construction fleets for reuse, retread or recycling.",
      specifications: [
        "Pairs / singles / cut pieces",
        "Inspection grades: A/B/C",
        "Bulk or container loading",
        "Cut-to-size service on request"
      ]
    },
    {
      name: "Rubber Butyl Tubes",
      image: "/assets/images/rubber/Rubber butyl tubes-min.jpeg",
      grades: ["Natural/Butyl mix", "Cleaned", "Baled"],
      description: "Post-consumer inner tubes sorted and baled, suitable for reclaim and compounding.",
      specifications: [
        "High butyl content",
        "Low contaminants (metal/valves removed options)",
        "Bale weight: 250–350 kg",
        "Consistent sourcing"
      ]
    },
    {
      name: "Rubber Granules",
      image: "/assets/images/rubber/Rubber granules-min.jpeg",
      grades: ["2–4 mm", "4–6 mm", "SBR", "EPDM mix"],
      description: "Screened SBR granules ideal for playground tiles, pavers and molded items.",
      specifications: [
        "Minimal textile contamination",
        "Uniform color/appearance",
        "Dust controlled packaging",
        "Jumbo bag supply"
      ]
    },
    {
      name: "Shredded Tyres",
      image: "/assets/images/rubber/Shredded tyres-min.jpeg",
      grades: ["50 mm", "100 mm", "150 mm", "Secondary Shreds", "Primary Shreds"],
      description: "Primary and secondary tyre shreds for civil works, TDF feed and further processing.",
      specifications: [
        "Size-controlled output",
        "Magnetic pre-separation",
        "Moisture controlled loads",
        "Bulk / loose loading"
      ]
    },
    {
      name: "Steel Tire Wire",
      image: "/assets/images/rubber/Tyre Wire-min.jpeg",
      grades: ["Clean Tire Wire", "<10% impurities", "<35% impurities"],
      description: "Recovered steel wire from tyre processing for remelting after rubber/textile removal.",
      specifications: [
        "Rubber content per spec (e.g., <5–12%)",
        "Magnetically cleaned",
        "High bulk density bales",
        "Mill-ready supply"
      ]
    },
    {
      name: "TDF Chips",
      image: "/assets/images/rubber/TDF Chips-min.jpg",
      grades: ["25×25 mm", "50×50 mm", "High-calorific"],
      description: "Tire-Derived Fuel chips manufactured to kiln and boiler feed specifications.",
      specifications: [
        "Calorific value: ~30–35 MJ/kg",
        "Wire content per spec (low / standard)",
        "Moisture: ≤2% typical",
        "Delivered in jumbo bags or bulk"
      ]
    }
  ];

  const certifications = [
    "ISO 9001:2015 Quality Management",
    "ISRI (Institute of Scrap Recycling Industries) Standards",
    "Dubai Municipality Waste Management License",
    "UAE Federal Customs Authority Registration",
    "International Environmental Compliance"
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-gray-900 to-gray-800 text-white">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-6">Our Products</h1>
            <p className="text-xl max-w-3xl mx-auto text-gray-200">
              Comprehensive range of high-quality ferrous, non-ferrous, and rubber materials 
              sourced globally and traded with complete transparency and reliability.
            </p>
          </div>
        </div>
      </section>

      {/* Ferrous Materials Section */}
      <section id="ferrous-materials" className="py-20 bg-white scroll-mt-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Ferrous Materials</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Iron and steel-based scrap materials with magnetic properties, ideal for steel production and manufacturing
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
            {ferrousProducts.map((product, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow overflow-hidden">
                <ImageWithFallback 
                  src={product.image} 
                  alt={product.name} 
                  className="w-full h-48 object-cover"
                />
                <CardContent className="p-6">
                  <h3 className="text-2xl font-semibold mb-3">{product.name}</h3>
                  <div className="mb-4">
                    {product.grades.map((grade, gradeIndex) => (
                      <Badge key={gradeIndex} variant="secondary" className="mr-2 mb-2 bg-[#fe2b32]/10 text-[#fe2b32]">
                        {grade}
                      </Badge>
                    ))}
                  </div>
                  <p className="text-gray-600 mb-4">{product.description}</p>
                  <div className="space-y-2">
                    {product.specifications.map((spec, specIndex) => (
                      <div key={specIndex} className="flex items-start">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-gray-600">{spec}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center">
            <ImageWithFallback 
              src="/assets/images/ferrous.jpeg"
              alt="Ferrous Materials" 
              className="rounded-lg shadow-lg w-full h-64 object-cover mb-6"
            />
          </div>
        </div>
      </section>

      {/* Non-Ferrous Materials Section */}
      <section id="non-ferrous-materials" className="py-20 bg-gray-50 scroll-mt-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Non-Ferrous Materials</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              High-value metals without magnetic properties, including copper, aluminum, brass, and specialty alloys
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {nonFerrousProducts.map((product, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow overflow-hidden">
                <ImageWithFallback 
                  src={product.image} 
                  alt={product.name} 
                  className="w-full h-48 object-cover"
                />
                <CardContent className="p-6">
                  <h3 className="text-2xl font-semibold mb-3">{product.name}</h3>
                  <div className="mb-4">
                    {product.grades.map((grade, gradeIndex) => (
                      <Badge key={gradeIndex} variant="secondary" className="mr-2 mb-2 bg-[#ffc533]/20 text-[#ffc533] border-[#ffc533]/30">
                        {grade}
                      </Badge>
                    ))}
                  </div>
                  <p className="text-gray-600 mb-4">{product.description}</p>
                  <div className="space-y-2">
                    {product.specifications.map((spec, specIndex) => (
                      <div key={specIndex} className="flex items-start">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-gray-600">{spec}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center">
            <ImageWithFallback 
              src="/assets/images/non-ferrous.jpeg"
              alt="Non-Ferrous Materials" 
              className="rounded-lg shadow-lg w-full h-64 object-cover object-center mb-6"
            />
          </div>
        </div>
      </section>

      {/* Rubber Materials Section */}
      <section id="rubber-materials" className="py-20 bg-white scroll-mt-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Rubber Materials</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive range of recycled rubber products from end-of-life tyres, including crumb rubber, granules, and specialized tyre derivatives for industrial and commercial applications
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {rubberProducts.map((product, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow overflow-hidden">
                <ImageWithFallback 
                  src={product.image} 
                  alt={product.name} 
                  className="w-full h-48 object-cover"
                />
                <CardContent className="p-6">
                  <h3 className="text-2xl font-semibold mb-3">{product.name}</h3>
                  <div className="mb-4">
                    {product.grades.map((grade, gradeIndex) => (
                      <Badge key={gradeIndex} variant="secondary" className="mr-2 mb-2 bg-[#ff41a5]/10 text-[#ff41a5] border-[#ff41a5]/30">
                        {grade}
                      </Badge>
                    ))}
                  </div>
                  <p className="text-gray-600 mb-4">{product.description}</p>
                  <div className="space-y-2">
                    {product.specifications.map((spec, specIndex) => (
                      <div key={specIndex} className="flex items-start">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-gray-600">{spec}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center">
            <ImageWithFallback 
              src="/assets/images/rubber.jpeg"
              alt="Rubber Materials" 
              className="rounded-lg shadow-lg w-full h-64 object-cover mb-6"
            />
          </div>
        </div>
      </section>

      {/* Quality Assurance Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">Quality Assurance & Certifications</h2>
              <p className="text-lg text-gray-600 mb-6">
                We maintain the highest standards of quality control and compliance with international 
                regulations to ensure that every shipment meets or exceeds customer expectations.
              </p>
              <div className="space-y-3 mb-8">
                {certifications.map((cert, index) => (
                  <div key={index} className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    <span>{cert}</span>
                  </div>
                ))}
              </div>
              <Button 
                size="lg" 
                className="bg-[#fe2b32] hover:bg-red-600"
                onClick={() => onPageChange('services')}
              >
                Learn About Our Services
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
            <div className="space-y-6">
              <Card className="p-6 border-l-4 border-[#fe2b32]">
                <h3 className="font-semibold mb-2">Material Testing</h3>
                <p className="text-gray-600">Comprehensive laboratory testing and chemical analysis for all materials.</p>
              </Card>
              <Card className="p-6 border-l-4 border-[#ffc533]">
                <h3 className="font-semibold mb-2">Documentation</h3>
                <p className="text-gray-600">Complete certificates of analysis and material traceability documentation.</p>
              </Card>
              <Card className="p-6 border-l-4 border-[#ff41a5]">
                <h3 className="font-semibold mb-2">Inspection Services</h3>
                <p className="text-gray-600">Third-party inspection services available for quality verification.</p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#fe2b32] to-[#ff41a5] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Need a Custom Quote?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Get competitive pricing for your specific material requirements. Our experts are ready to help.
          </p>
          <Button 
            size="lg" 
            variant="secondary"
            className="bg-white text-[#fe2b32] hover:bg-gray-100"
            onClick={() => onPageChange('contact')}
          >
            Request Quote Now
          </Button>
        </div>
      </section>
    </div>
  );
}